import { useState, useEffect } from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL || 'https://epstein-files-12.s3.us-east-2.amazonaws.com/raw/Seventh_Production/IMAGES';

const PageContainer = styled.div`
  padding: 3rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  border-bottom: 2px solid #C4B5A0;
  padding-bottom: 1rem;
`;

const FileExplorer = styled.div`
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  box-shadow: 
    inset 0 1px 3px rgba(0, 0, 0, 0.05),
    1px 1px 5px rgba(0, 0, 0, 0.08);
  min-height: 500px;
`;

const Breadcrumb = styled.div`
  padding: 1rem;
  background: #F0E8D8;
  border-bottom: 1.5px solid #C4B5A0;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BreadcrumbItem = styled.span`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.brownDark};
  }
  color: ${props => props.$active ? '#333' : '#8A7E70'};
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
`;

const FileList = styled.div`
  padding: 1rem;
`;

const FileItem = styled.div`
  padding: 1rem;
  border-bottom: 1px dashed #D4CFC4;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #FDF9F0;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const FileIcon = styled.span`
  font-size: 1.5rem;
  width: 30px;
  text-align: center;
`;

const FileName = styled.span`
  flex: 1;
  font-family: 'Courier New', monospace;
  font-weight: ${props => props.$isFolder ? 'bold' : 'normal'};
`;

const FileSize = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: #8A7E70;
  min-width: 80px;
  text-align: right;
`;

const FileDate = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: #8A7E70;
  min-width: 100px;
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  font-family: 'Courier New', monospace;
  color: #8A7E70;
`;

// Reuse Modal Styles from Search.js
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: #FFFEF9;
  width: 95%;
  max-width: 1400px;
  height: 90vh;
  overflow-y: auto;
  padding: 3rem;
  border-radius: 2px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 2.5rem;
  cursor: pointer;
  color: #8A7E70;
  line-height: 1;
  
  &:hover {
    color: #333;
  }
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  margin: 0 auto;
  border: 1px solid #C4B5A0;
  background: #fff;
`;

const ResultSection = styled.div`
  margin-bottom: 1rem;
  word-wrap: break-word;
`;

const SectionLabel = styled.span`
  font-weight: bold;
  color: #8A7E70;
  margin-right: 0.5rem;
  display: block;
  margin-bottom: 0.25rem;
`;

function Explorer() {
  const [currentFolder, setCurrentFolder] = useState(null); // null = root
  const [folders, setFolders] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);

  // Fetch and organize documents on load
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/fuzzy_search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            query: '*', 
            limit: 1000,
            // Optimize: Exclude heavy fields
            include_fields: ['file_path', 'summary', 'dates', 'people', 'locations']
          }), 
        });
        
        if (!response.ok) throw new Error('Failed to fetch documents');
        
        const data = await response.json();
        const organized = organizeDocuments(data.results || []);
        setFolders(organized);
      } catch (err) {
        console.error('Explorer error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedFile) {
        setSelectedFile(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFile]);

  const organizeDocuments = (docs) => {
    const categories = {
      'Court & Legal': [],
      'Communication': [],
      'Financial': [],
      'Flight Logs': [],
      'Images & Media': [],
      'Other': []
    };

    docs.forEach(doc => {
      const summary = (doc.summary || '').toLowerCase();
      // const content = (doc.ocr_content || '').toLowerCase(); // Removed dependency on content for sorting
      
      if (summary.includes('transcript') || summary.includes('deposition') || summary.includes('affidavit') || 
          summary.includes('court') || summary.includes('lawsuit') || summary.includes('plea') || 
          summary.includes('testimony')) {
        categories['Court & Legal'].push(doc);
      } else if (summary.includes('email') || summary.includes('letter') || summary.includes('correspondence') || 
                 summary.includes('message')) {
        categories['Communication'].push(doc);
      } else if (summary.includes('financial') || summary.includes('payment') || summary.includes('bank') || 
                 summary.includes('check') || summary.includes('transaction')) {
        categories['Financial'].push(doc);
      } else if (summary.includes('flight') || summary.includes('pilot') || summary.includes('aircraft') || 
                 summary.includes('jet') || summary.includes('airport') || summary.includes('aviation')) {
        categories['Flight Logs'].push(doc);
      } else if (summary.includes('photograph') || summary.includes('image') || summary.includes('picture') || 
                 summary.includes('photo')) {
        categories['Images & Media'].push(doc);
      } else {
        categories['Other'].push(doc);
      }
    });

    // Remove empty categories
    Object.keys(categories).forEach(key => {
      if (categories[key].length === 0) delete categories[key];
    });

    return categories;
  };

  const handleFolderClick = (folderName) => {
    setCurrentFolder(folderName);
  };

  const handleBreadcrumbClick = () => {
    setCurrentFolder(null);
  };

  const handleFileClick = async (file) => {
    // Construct S3 URL
    const cleanBase = S3_BASE_URL.replace(/\/$/, '');
    const cleanPath = file.file_path.replace(/^\//, '');
    const imageUrl = `${cleanBase}/${cleanPath}`;
    
    // Initial open with what we have
    setSelectedFile({ ...file, imageUrl, loadingContent: !file.ocr_content });

    // Lazy load content if missing
    if (!file.ocr_content) {
      try {
        const response = await fetch(`${API_BASE_URL}/fuzzy_search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            // Search for specific file path to get full content
            query: file.file_path, 
            limit: 1,
            include_fields: ['ocr_content', 'file_path'] // We only need content
          }), 
        });

        if (response.ok) {
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            // Update the selected file with full content
            setSelectedFile(prev => ({
              ...prev,
              ocr_content: data.results[0].ocr_content,
              loadingContent: false
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching full content:", err);
        setSelectedFile(prev => ({ ...prev, loadingContent: false }));
      }
    }
  };


  return (
    <>
      <SEO 
        title="Document Explorer" 
        description="Browse the full directory of declassified documents in the Epstein Archive."
      />
      <PageContainer>
        <Title>Document Explorer</Title>
        
        <FileExplorer>
          <Breadcrumb>
            <BreadcrumbItem onClick={handleBreadcrumbClick} $active={!currentFolder}>
              📁 root
            </BreadcrumbItem>
            {currentFolder && (
              <>
                <span>/</span>
                <BreadcrumbItem $active={true}>
                  {currentFolder}
                </BreadcrumbItem>
              </>
            )}
          </Breadcrumb>

          <FileList>
            {loading ? (
              <LoadingMessage>Loading documents...</LoadingMessage>
            ) : !currentFolder ? (
              // Folder View
              Object.keys(folders).map(folder => (
                <FileItem key={folder} onClick={() => handleFolderClick(folder)}>
                  <FileIcon>📁</FileIcon>
                  <FileName $isFolder={true}>{folder}</FileName>
                  <FileSize>{folders[folder].length} items</FileSize>
                  <FileDate>-</FileDate>
                </FileItem>
              ))
            ) : (
              // File View
              folders[currentFolder]?.map((file, index) => (
                <FileItem key={index} onClick={() => handleFileClick(file)}>
                  <FileIcon>📄</FileIcon>
                  <FileName>{file.file_path.split('/').pop()}</FileName>
                  <FileSize>
                    {(file.ocr_content?.length / 1024).toFixed(1)} KB
                  </FileSize>
                  <FileDate>{file.dates?.split(';')[0] || '-'}</FileDate>
                </FileItem>
              ))
            )}
          </FileList>
        </FileExplorer>
      </PageContainer>

      {/* File Viewer Modal */}
      {selectedFile && (
        <ModalOverlay onClick={() => setSelectedFile(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setSelectedFile(null)}>×</CloseButton>
            
            <h2 style={{ borderBottom: '2px solid #C4B5A0', paddingBottom: '1rem', fontSize: '1.2rem', fontFamily: 'Courier New' }}>
              {selectedFile.file_path}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <div style={{ background: '#F9F5EB', padding: '1rem', display: 'flex', justifyContent: 'center' }}>
                <ModalImage 
                  src={selectedFile.imageUrl} 
                  alt="Document"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML = '<div style="padding: 2rem; color: #888;">Image preview not available</div>';
                  }}
                />
              </div>

              <div>
                {selectedFile.summary && (
                  <ResultSection>
                    <SectionLabel>Summary:</SectionLabel>
                    {selectedFile.summary}
                  </ResultSection>
                )}
                {selectedFile.people && (
                  <ResultSection>
                    <SectionLabel>People:</SectionLabel>
                    {selectedFile.people}
                  </ResultSection>
                )}
                {selectedFile.dates && (
                  <ResultSection>
                    <SectionLabel>Dates:</SectionLabel>
                    {selectedFile.dates}
                  </ResultSection>
                )}
                {selectedFile.ocr_content && (
                  <ResultSection>
                    <SectionLabel>Full Content:</SectionLabel>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: '1rem', padding: '1rem', background: '#f5f5f5', borderRadius: '4px', maxHeight: '300px', overflowY: 'auto', fontFamily: 'Courier New', fontSize: '0.9rem' }}>
                      {selectedFile.ocr_content}
                    </div>
                  </ResultSection>
                )}
                {selectedFile.loadingContent && (
                  <ResultSection>
                    <SectionLabel>Full Content:</SectionLabel>
                    <div style={{ padding: '1rem', color: '#888', fontStyle: 'italic' }}>
                      Loading full content...
                    </div>
                  </ResultSection>
                )}
              </div>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

export default Explorer;

